<script setup lang="ts">
// Styles and dependencies for splitpanes
import 'splitpanes/dist/splitpanes.css';
import { Splitpanes, Pane } from 'splitpanes';

// Components
import Chat from "@/components/ai/Chat.vue";
import MobileSwitcher from "@/components/_common/ThreeColumnsMobileSwitcher.vue";

// Composables
import { useThreeColumnLayoutLayout } from "@/composables/useThreeColumnLayoutLayout";
import { useMobileSwitch } from "@/composables/useMobileSwitch";
import { useDelayedState } from "@/composables/useDelayedState";
import { usePreventTextSelection } from "@/composables/usePreventTextSelection";

// Setup split pane layout
const {
  isLeftOpen,
  isRightOpen,
  leftSize,
  rightSize,
  centerSize,
  handleResizeSidebars
} = useThreeColumnLayoutLayout();

// Setup mobile switcher
const { mobileSwitchValue } = useMobileSwitch({ isLeftOpen, isRightOpen });

// Setup delayed initialization for animations
const { state: isInitialized } = useDelayedState({
  delay: 400,
  initialValue: false,
  triggerOnMount: true
});

// Setup text selection prevention
const { startPreventingSelection, stopPreventingSelection } = usePreventTextSelection();

// Handle resize start
const handleResize = (params: any) => {
  startPreventingSelection();
  handleResizeSidebars(params);
};

// Handle resize stop
const handleResizeStop = (params: any) => {
  stopPreventingSelection(400);
};
</script>

<style>
.splitpanes.default-theme .splitpanes__splitter {
  @apply relative shrink-0 bg-gpx_gray-900 box-border;

  touch-action: none;
  cursor: col-resize;
}

.splitpanes.default-theme .splitpanes--horizontal > .splitpanes__splitter {
  cursor: row-resize;
}

.splitpanes.default-theme .splitpanes__splitter::before,
.splitpanes.default-theme .splitpanes__splitter::after {
  content: "";
  display:none;
  position: absolute;
  top: 50%;
  left: 50%;
  background-color: rgba(0, 0, 0, 0.45);
  transition: background-color 0.3s;
  pointer-events: none;
}

.splitpanes.default-theme .splitpanes__splitter:hover::before,
.splitpanes.default-theme .splitpanes__splitter:hover::after {
  @apply bg-gpx_red-200;
}
.splitpanes.default-theme .splitpanes__pane:nth-child(3) {
  overflow: visible !important;
}

.splitpanes.default-theme .splitpanes__pane{
  background-color: rgba(255, 255, 255, 1);
}

.hide-left-splitter .splitpanes__splitter:nth-child(2) {
  display: none !important;
}
.hide-right-splitter .splitpanes__splitter:nth-child(4) {
  display: none !important;
}

/* disable animation */
.splitpanes:not(.initialized) .splitpanes__pane {
  transition: none !important;
}
</style>

<template>
  <div class="relative h-layout-mobile sm:h-layout-tablet md:h-layout-desktop overflow-hidden">
    <Splitpanes
      @resize="handleResize"
      @resized="handleResizeStop"
      class="default-theme hide-splitter-on-mobile h-layout-mobile sm:h-layout-tablet md:h-layout-desktop"
      :class="{
        'hide-left-splitter': !isLeftOpen,
        'hide-right-splitter': !isRightOpen,
        'initialized': isInitialized
      }"
    >
      <Pane key="left" :size="leftSize">
        <div class="flex h-full w-full flex-col overflow-hidden">
          <div class="top-bar flex w-full shrink-0 items-stretch">
            <slot name="leftTop" />
          </div>
          <div class="min-h-0 flex-1 overflow-hidden">
            <slot name="left" />
          </div>
        </div>
      </Pane>
      <Pane key="center" :size="centerSize">
        <div class="relative flex h-layout-mobile-visible flex-col sm:h-layout-tablet md:h-layout-desktop">
          <div class="top-bar sticky top-0 z-10 w-full shrink-0">
            <slot name="rightTop" />
          </div>
          <div class="z-0 min-h-0 w-full flex-1 overflow-hidden">
            <slot name="right" />
          </div>
        </div>
      </Pane>
      <Pane key="right" v-show="$can('use', 'Ai')" :size="rightSize">
        <template v-if="$can('use', 'Ai')">
          <Chat @close="isRightOpen = false" />
        </template>
      </Pane>
    </Splitpanes>
    <button
      @click="isLeftOpen = !isLeftOpen"
      @keydown.enter="isLeftOpen = !isLeftOpen"
      class="absolute z-[1000] top-1/2 -translate-y-1/2 bg-gpx_gray-900 border border-gpx_gray-700 hover:border-gpx_gray-600 text-gpx_gray-600 hover:text-gpx_gray-200 rounded-md p-2 shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ease-out duration-200 hidden sm:block"
      :style="{ left: isLeftOpen ? 'calc(' + leftSize + '% - 12px)' : '-3px' }"
      aria-label="Left sidebar toggle"
      tabindex="0"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-4 w-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        :class="{ 'rotate-180': !isLeftOpen }"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
      </svg>
    </button>
    <MobileSwitcher  class="absolute bottom-4  right-2" v-model="mobileSwitchValue" />
  </div>
</template>
